import {
	takeAskQuestionStashFor,
	writeAskQuestionStashFor,
} from "~/modules/shared/ask/askQuestionStash";

// askQuestionStashKeys.prompt is shared across the whole
// app (studio video library, clipboard view, ask-page) and carries no
// record of which page it was written for. Auto-submitting whatever it
// holds on page load - rather than just prefilling a box, which every
// other reader of that stash does - would let an unrelated leftover stash
// fire as a real, irreversible public question the moment this page loads.
// This target marker scopes the handoff to one specific (handle) this
// stash write was actually for, so ask.tsx only acts on it when it matches.
const TARGET_KEY = "askgvt:ask-page:question-handoff-target";

function targetStorage(): Storage | undefined {
	return typeof window === "undefined" ? undefined : window.sessionStorage;
}

/** Called right before navigating away (sign-in) or re-rendering in place (a 402). */
export function stashQuestionForHandoff(handle: string, prompt: string): void {
	writeAskQuestionStashFor("prompt", prompt);
	try {
		targetStorage()?.setItem(TARGET_KEY, handle);
	} catch {
		// Private browsing, quota, or blocked storage.
	}
}

/**
 * One-shot: returns the stashed question text only if it was stashed for
 * this exact handle, and clears the target marker either way. Never reads
 * the underlying stash at all when the marker doesn't match, so an
 * unrelated stash (from Studio, say) is left untouched for whatever else
 * may be waiting to consume it. Visiting a different creator's page while
 * a handoff for this one is still pending clears it without restoring the
 * question there either - losing an in-flight handoff is the safe failure
 * mode, the alternative (auto-firing it on the wrong page) is not.
 */
export function takeQuestionHandoffFor(handle: string): string | undefined {
	const storage = targetStorage();
	if (!storage) {
		return undefined;
	}

	let target: string | null;
	try {
		target = storage.getItem(TARGET_KEY);
		storage.removeItem(TARGET_KEY);
	} catch {
		return undefined;
	}

	if (target !== handle) {
		return undefined;
	}

	return takeAskQuestionStashFor("prompt", storage);
}
